'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Heart } from 'lucide-react';
import { MobilePageHeader } from '@/components/mobile/mobile-header';
import { QuantitySelector } from '@/components/product/quantity-selector';
import { IconView } from '@/components/admin/icon-picker';
import { Button } from '@/components/ui/button';
import { useCart } from '@/hooks/useCart';
import { formatPrice } from '@/lib/utils';
import { resolvePrice } from '@/presenters/price.presenter';
import type { Product } from '@/models';

interface MobileProductDetailViewProps {
  product: Product;
}

export function MobileProductDetailView({ product }: MobileProductDetailViewProps) {
  const price = resolvePrice(product);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(0);
  const { addItem } = useCart();

  const attributes = product.attributes ?? [];
  const colors = product.colors ?? [];

  const handleAdd = () => {
    addItem({
      productId: product.id,
      variantId: 'default',
      productName: product.name,
      variantName: colors[selectedColor]?.nameAr ?? '',
      price,
      showPrice: product.showPrice,
      imageUrl: product.imageUrl,
      quantity,
    });
  };

  return (
    <div dir="rtl" className="min-h-screen bg-background pb-28 md:hidden">
      <MobilePageHeader title="تفاصيل المنتج" backHref="/products" />

      <div className="relative mx-4 aspect-square rounded-3xl bg-muted">
        <Image
          src={product.imageUrl || '/products/galaxy-s24-ultra.svg'}
          alt={product.name}
          fill
          className="object-contain p-8"
          unoptimized
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="إضافة للمفضلة"
          className="absolute top-3 left-3 rounded-full"
        >
          <Heart className="text-muted-foreground" />
        </Button>
      </div>

      <div className="space-y-3 px-4 pt-5">
        <p className="text-xs font-bold text-muted-foreground">{product.brandName}</p>
        <h1 className="font-display text-xl font-black">{product.name}</h1>
        {product.showPrice && <p className="text-2xl font-black text-primary">{formatPrice(price)} دج</p>}

        {colors.length > 0 && (
          <div className="space-y-2 pt-2">
            <p className="text-xs font-bold text-muted-foreground">اختر اللون</p>
            <div className="flex gap-2">
              {colors.map((color, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={color.nameAr}
                  onClick={() => setSelectedColor(index)}
                  className={`h-8 w-8 rounded-full border-2 ${
                    selectedColor === index ? 'border-primary' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color.hex }}
                />
              ))}
            </div>
          </div>
        )}

        {attributes.length > 0 && (
          <div className="space-y-2 pt-4">
            {attributes.map((attribute, index) => (
              <div key={index} className="flex items-center justify-between rounded-xl bg-muted p-3">
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  <IconView name={attribute.icon} className="h-4 w-4" />
                  {attribute.key}
                </span>
                <span className="text-xs font-bold">{attribute.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-border bg-background p-4">
        <QuantitySelector quantity={quantity} onQuantityChange={setQuantity} />
        <Button onClick={handleAdd} className="flex-1 rounded-2xl py-3.5 text-sm font-black">
          أضف إلى الطلب
        </Button>
      </div>
    </div>
  );
}
